import clsx from 'clsx'
import { TableBody, type TableBodyProps, type TableProps } from 'primitives/table'
import { Typography } from 'primitives/typography'
import { memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTableRowVirtualizer } from '../../hooks/use-table-row-virtualizer'
import { useTableRows } from '../../hooks/use-table-rows'
import type {
  DataTableColumnVirtualizer,
  DataTableInstance,
  DataTableRow,
  DataTableRowData,
  DataTableVirtualItem,
} from '../../types'
import { DataTableBodyRow, MemoDataTableBodyRow } from './table-body-row'
import classes from './table-body.module.css'

export interface DataTableBodyProps<TData extends DataTableRowData> extends TableBodyProps {
  columnVirtualizer?: DataTableColumnVirtualizer
  table: DataTableInstance<TData>
  tableProps: Partial<TableProps>
}

export const DataTableBody = <TData extends DataTableRowData>({
  columnVirtualizer,
  table,
  tableProps,
  ...rest
}: DataTableBodyProps<TData>) => {
  const {
    getBottomRows,
    getIsSomeRowsPinned,
    getRowModel,
    getState,
    getTopRows,
    options: {
      enableStickyFooter,
      enableStickyHeader,
      layoutMode,
      localization,

      memoMode,
      renderDetailPanel,
      renderEmptyRowsFallback,
      rowPinningDisplayMode,
    },
    refs: { tableFooterRef, tableHeadRef },
  } = table
  const { columnFilters, globalFilter, rowPinning } = getState()

  const tableBodyProps = {
    table,
    ...rest,
  }

  const tableHeadHeight = (enableStickyHeader && tableHeadRef.current?.clientHeight) || 0
  const tableFooterHeight = (enableStickyFooter && tableFooterRef.current?.clientHeight) || 0

  const pinnedRowIds = useMemo(() => {
    if (!rowPinning.bottom?.length && !rowPinning.top?.length) return []
    return getRowModel()
      .rows.filter((row) => row.getIsPinned())
      .map((r) => r.id)
  }, [rowPinning, getRowModel().rows])

  const rows = useTableRows(table)

  const rowVirtualizer = useTableRowVirtualizer(table, rows)

  const { virtualRows } = rowVirtualizer ?? {}

  const commonRowProps = {
    columnVirtualizer,
    numRows: rows.length,
    table,
    tableProps,
  }

  return (
    <>
      {!rowPinningDisplayMode?.includes('sticky') && getIsSomeRowsPinned('top') && (
        <TableBody
          {...tableBodyProps}
          style={{ top: `calc(${tableHeadHeight}px * 1px - 1px)` }}
          className={clsx(
            classes.pinned,
            layoutMode?.startsWith('grid') && 'grid',
            tableBodyProps?.className,
          )}
        >
          {getTopRows().map((row, renderedRowIndex) => {
            const rowProps = {
              ...commonRowProps,
              renderedRowIndex,
              row,
            }
            return memoMode === 'rows' ? (
              <MemoDataTableBodyRow key={row.id} {...rowProps} />
            ) : (
              <DataTableBodyRow key={row.id} {...rowProps} />
            )
          })}
        </TableBody>
      )}
      <TableBody
        {...tableBodyProps}
        style={
          rowVirtualizer
            ? {
                height: rowVirtualizer ? `${rowVirtualizer.getTotalSize()}px` : undefined,
              }
            : {}
        }
        className={clsx(
          'relative bg-background',
          layoutMode?.startsWith('grid') && 'grid',
          !rows.length && 'min-h-[100px]',
          tableBodyProps?.className,
        )}
      >
        {tableBodyProps?.children ??
          (!rows.length ? (
            <div className="w-full">
              {renderEmptyRowsFallback?.({ table }) ??
                createPortal(
                  <Typography
                    style={{
                      maxWidth: `100vw`,
                    }}
                    className={clsx(classes['empty-row-td-content'], 'text-secondary')}
                  >
                    {globalFilter || columnFilters.length
                      ? localization.noResultsFound
                      : localization.noRecordsToDisplay}
                  </Typography>,
                  document.getElementById('bottom-toolbar') || document.body,
                )}
            </div>
          ) : (
            (virtualRows ?? rows).map((rowOrVirtualRow, renderedRowIndex) => {
                if (rowVirtualizer) {
                  if (renderDetailPanel) {
                    if (rowOrVirtualRow.index % 2 === 1) {
                      return null
                    }
                    renderedRowIndex = rowOrVirtualRow.index / 2
                  } else {
                    renderedRowIndex = rowOrVirtualRow.index
                  }
                }
                const row = rowVirtualizer
                  ? rows[renderedRowIndex]
                  : (rowOrVirtualRow as DataTableRow<TData>)
                const props = {
                  ...commonRowProps,
                  pinnedRowIds,
                  renderedRowIndex,
                  row,
                  rowVirtualizer,
                  virtualRow: rowVirtualizer
                    ? (rowOrVirtualRow as DataTableVirtualItem)
                    : undefined,
                }
                const key = `${row.id}-${row.index}`
                return memoMode === 'rows' ? (
                  <MemoDataTableBodyRow key={key} {...props} />
                ) : (
                  <DataTableBodyRow key={key} {...props} />
                )
              })
          ))}
      </TableBody>
      {!rowPinningDisplayMode?.includes('sticky') && getIsSomeRowsPinned('bottom') && (
        <TableBody
          {...tableBodyProps}
          style={{
            bottom: `[calc(${tableFooterHeight}*1px-1px)]`,
          }}
          className={clsx(
            classes.pinned,
            layoutMode?.startsWith('grid') && 'grid',
            tableBodyProps?.className,
          )}
        >
          {getBottomRows().map((row, renderedRowIndex) => {
            const props = {
              ...commonRowProps,
              renderedRowIndex,
              row,
            }
            return memoMode === 'rows' ? (
              <MemoDataTableBodyRow key={row.id} {...props} />
            ) : (
              <DataTableBodyRow key={row.id} {...props} />
            )
          })}
        </TableBody>
      )}
    </>
  )
}

export const MemoDataTableBody = memo(
  DataTableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof DataTableBody
